import { useEffect, useRef, useState } from 'react';
import { ImageOff } from 'lucide-react';
import { loadPackTextureUrl } from '@/services/packAssets';
import styles from './PackThumbnail.module.scss';

interface PackThumbnailProps {
  groupId: string;
  path?: string | null;
  alt?: string;
  size?: number;
}

/**
 * Vignette d'une texture de pack, chargée via blob authentifié (le JWT ne passe pas dans <img src>).
 * Lazy-load : la texture n'est chargée que lorsque la vignette entre dans le viewport
 * (évite des centaines de fetch simultanés, ex. palette de recette).
 */
export function PackThumbnail({ groupId, path, alt, size = 40 }: PackThumbnailProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin: '250px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    let active = true;
    setFailed(false);
    setUrl(null);
    if (visible && path) {
      loadPackTextureUrl(groupId, path).then((u) => {
        if (!active) return;
        if (u) setUrl(u);
        else setFailed(true);
      });
    }
    return () => {
      active = false;
    };
  }, [visible, groupId, path]);

  const style = { width: size, height: size };

  return (
    <span ref={ref} className={styles.wrap} style={style}>
      {!path || failed ? (
        <span className={styles.placeholder}>
          <ImageOff size={Math.round(size * 0.4)} />
        </span>
      ) : !url ? (
        <span className={styles.loading} />
      ) : (
        <img className={styles.img} src={url} alt={alt || ''} />
      )}
    </span>
  );
}
